import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useGetAuthoringComponentQuery, useListComponentsQuery } from '../../api/authoring'
import type { Manifest } from '../../api/authoring'
import { payloadId } from '../../api/useIntent'
import { aOrAn, LISTING_LAG } from '../../model/labels'
import { inlineable, typeProse } from '../../model/manifest'
import { CreateComponentDialog } from '../../ui/CreateComponentDialog'
import { InlineSetup } from '../../ui/InlineSetup'
import { Loading, QueryError } from '../../ui/QueryState'

/**
 * What may go here, by name, with the model's question answered for the
 * author.
 *
 * **The link-or-inline call is made here, not asked.** Build mode's
 * picker leads with two tabs — "link an existing component" and "define
 * one inline" — which is the first question it asks and the one an owner
 * has no stake in at all. So the words never appear: a new block is
 * defined in place where its type allows it, and linked where the type
 * carries identity, which is the rule the model already states.
 *
 * **What an author does have a stake in is reuse**, and it is a
 * different question wearing the same clothes. "Start a new one" and
 * "use the one I already wrote" is a decision about content — the
 * opening hours that appear on three pages are *one* set of opening
 * hours — and a picker that answered it by silently making a fourth copy
 * would take the model's best property away from the reader who most
 * benefits from it. So both are offered, in those words, and which
 * mechanism each one uses is still never mentioned.
 *
 * The one consequence of reuse that is genuinely the author's is said
 * plainly where they choose it: editing a block that appears in three
 * places changes all three. That is not model vocabulary, it is what
 * will happen.
 *
 * Which types are offered is the manifest's answer — the holding node's
 * palette — and what each is called is the type's own. Nothing here
 * names a type.
 */
export function AddBlockDialog({
  tenant,
  open,
  where,
  palette,
  manifest,
  onClose,
  onLink,
  onInline,
  busy,
}: {
  tenant: string
  open: boolean
  /** What is being added to, in words, for the dialog's own title. */
  where: string
  /** The types the holding node may contain. */
  palette: string[]
  manifest: Manifest | undefined
  onClose: () => void
  onLink: (typeName: string, targetId: string) => void
  /**
   * A block defined in place: the type, the fields its first revision
   * must state, and the references a pattern consumer needs.
   */
  onInline: (
    typeName: string,
    fields?: Record<string, unknown>,
    references?: CommandReference[],
  ) => void
  busy: boolean
}) {
  // Which type the author picked, when picking it was not the whole
  // answer. A linked type needs one of the existing components named, so
  // the dialog has a second step; an inline one is placed on the spot
  // and never reaches it.
  const [choosing, setChoosing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (open) {
      setChoosing(null)
      setCreating(false)
    }
  }, [open])

  // What the tenant already holds of the chosen type — the reuse half.
  // A type that is usually written in place will often have none, and
  // that is an ordinary answer rather than an empty state.
  const listing = useListComponentsQuery(
    { tenant, type: choosing ?? '', limit: 100 },
    { skip: !open || !choosing },
  )
  const components = listing.data?.components ?? []
  const prose = choosing ? typeProse(manifest, choosing) : undefined

  // Every type gets the second step now, because every type can be
  // reused. What differs is what the step *offers*: a type that may be
  // defined in place leads with starting a fresh one, and a type that
  // carries identity leads with adding one to the library.
  const pick = (typeName: string) => setChoosing(typeName)

  const canStartFresh = choosing ? inlineable(manifest, choosing) : false
  const patternSetup = choosing ? patternSetupOf(manifest, choosing) : undefined

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{choosing && prose ? `Add ${aOrAn(prose.title.toLowerCase())}` : `Add to ${where}`}</DialogTitle>
      <DialogContent dividers>
        {!choosing ? (
          <Stack spacing={1}>
            {palette.length === 0 && (
              <Alert severity="info">Nothing can be added here.</Alert>
            )}
            {palette.map((name) => {
              const entry = typeProse(manifest, name)
              return (
                <Paper key={name} variant="outlined">
                  <ListItemButton disabled={busy} onClick={() => pick(name)} sx={{ py: 1.5 }}>
                    <ListItemText primary={entry.title} secondary={entry.description} />
                  </ListItemButton>
                </Paper>
              )
            })}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setChoosing(null)}>
              Something else
            </Button>

            {/* Starting fresh, where the type allows a block that lives
                on this page alone. It is the ordinary case and it leads. */}
            {canStartFresh && patternSetup ? (
              <PatternBlockSetup
                tenant={tenant}
                manifest={manifest}
                setup={patternSetup}
                busy={busy}
                onAdd={(references) => onInline(choosing, undefined, references)}
              />
            ) : canStartFresh ? (
              <InlineSetup
                manifest={manifest}
                typeName={choosing}
                label={`Start a new ${prose?.title.toLowerCase()}`}
                busy={busy}
                onPlace={(fields) => onInline(choosing, fields)}
              />
            ) : (
              <Stack direction="row">
                <Button variant="contained" startIcon={<AddIcon />} disabled={busy} onClick={() => setCreating(true)}>
                  Add a new {prose?.title.toLowerCase()}
                </Button>
              </Stack>
            )}

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                or use one you already have
              </Typography>
            </Divider>

            {listing.isLoading && <Loading label="Looking…" />}
            {listing.error && <QueryError error={listing.error} title="Could not look that up" />}

            {!listing.isLoading && !listing.error && components.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                There are none to reuse yet.
              </Typography>
            )}

            {components.length > 0 && (
              <>
                <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
                  {components.map((component) => (
                    <ListItemButton
                      key={component.id}
                      disabled={busy}
                      onClick={() => onLink(choosing, component.id)}
                    >
                      <ListItemText primary={component.label ?? component.id} />
                    </ListItemButton>
                  ))}
                </List>

                {/* The one thing about reuse an author must know, in
                    what will happen rather than in what it is called. */}
                <Alert severity="info">
                  This puts the same one on this page too. Editing it changes it everywhere it appears.
                </Alert>
              </>
            )}

            {canStartFresh && (
              <Typography variant="caption" color="text.secondary">
                Only blocks saved for reuse are listed. A block written directly on another page belongs to that page.
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              {LISTING_LAG}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>

      {/* Placed straight from the create's own answer, never by looking
          for it afterwards. */}
      {choosing && (
        <CreateComponentDialog
          tenant={tenant}
          open={creating}
          manifest={manifest}
          types={[choosing]}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false)
            onLink(choosing, created.id)
          }}
        />
      )}
    </Dialog>
  )
}

/**
 * The two pattern consumers are meaningful only when their required
 * references are present. Collect those choices before placement so the
 * first revision is a usable block rather than an empty mechanism an
 * author has to diagnose in the editor afterwards.
 */
function PatternBlockSetup({
  tenant,
  manifest,
  setup,
  busy,
  onAdd,
}: {
  tenant: string
  manifest: Manifest | undefined
  setup: PatternSetup
  busy: boolean
  onAdd: (references: CommandReference[]) => void
}) {
  const repeating = setup.kind === 'repeating'
  const patternType = manifest?.types.find((entry) => entry.declaresPatterns)?.name ?? ''
  const patterns = useListComponentsQuery(
    { tenant, type: patternType, limit: 100 },
    { skip: !patternType },
  )
  const [patternId, setPatternId] = useState('')
  const pattern = useGetAuthoringComponentQuery(
    { tenant, id: patternId },
    { skip: !patternId },
  )
  const inputType = inputTypeOf(pattern.data?.fields)
  const inputs = useListComponentsQuery(
    { tenant, type: repeating ? setup.targetType : inputType, limit: 100 },
    { skip: !patternId || (!repeating && !inputType) },
  )
  const [inputId, setInputId] = useState('')

  useEffect(() => setInputId(''), [patternId])

  const patternOptions = patterns.data?.components ?? []
  const inputOptions = inputs.data?.components ?? []
  const ready = Boolean(patternId && inputId)

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" fontWeight={600}>Set up this block</Typography>
          <Typography variant="body2" color="text.secondary">
            {repeating
              ? 'Choose how each item looks, then choose the collection to repeat.'
              : 'Choose how the content looks, then choose the content to show.'}
          </Typography>
        </Stack>

        <TextField
          select
          label="How should it look?"
          value={patternId}
          onChange={(event) => setPatternId(event.target.value)}
          disabled={busy || patterns.isLoading || patternOptions.length === 0}
          helperText={
            patternOptions.length === 0
              ? 'No layouts are available yet. Ask your agency to add a pattern first.'
              : 'The pattern decides how it is laid out.'
          }
          fullWidth
        >
          {patternOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>{option.label ?? option.id}</MenuItem>
          ))}
        </TextField>

        {patternId && pattern.isLoading && <Loading label="Reading the pattern…" />}
        {pattern.error && <QueryError error={pattern.error} title="Could not read that pattern" />}
        {patternId && pattern.data && !inputType && (
          <Alert severity="warning">This pattern cannot be used here, because it does not say what content it shows. Ask your agency.</Alert>
        )}

        <TextField
          select
          label={repeating ? 'Which collection should repeat?' : 'Which content should it show?'}
          value={inputId}
          onChange={(event) => setInputId(event.target.value)}
          disabled={busy || !patternId || !inputType || inputs.isLoading || inputOptions.length === 0}
          helperText={
            !patternId
              ? 'Choose a pattern first. Only content that fits it will be listed.'
              : inputOptions.length === 0
                ? `No ${repeating ? 'lists' : typeProse(manifest, inputType).plural.toLowerCase()} are available yet.`
                : repeating
                  ? `Every item in this list is shown using the chosen pattern.`
                  : `Only ${typeProse(manifest, inputType).plural.toLowerCase()} fit the chosen pattern.`
          }
          fullWidth
        >
          {inputOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>{option.label ?? option.id}</MenuItem>
          ))}
        </TextField>

        <Stack direction="row">
          <Button
            variant="contained"
            disabled={busy || !ready}
            onClick={() =>
              onAdd([
                { edgeId: 'declaration', name: 'declaration', id: payloadId(tenant, patternId) },
                {
                  edgeId: repeating ? 'source' : 'input',
                  name: repeating ? 'source' : 'input',
                  id: payloadId(tenant, inputId),
                },
              ])
            }
          >
            Add {repeating ? 'repeating block' : 'pattern block'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

function inputTypeOf(fields: Record<string, unknown> | undefined): string {
  const input = fields?.input
  if (!input || typeof input !== 'object' || Array.isArray(input)) return ''
  const accepts = (input as Record<string, unknown>).accepts
  return typeof accepts === 'string' ? accepts : ''
}

interface CommandReference {
  edgeId: string
  name: string
  /** Command payloads carry the tenant-qualified id, unlike create input. */
  id: string
}

interface PatternSetup {
  kind: 'single' | 'repeating'
  targetType: string
}

/** Recognise the two pattern consumers from their declared dependencies. */
function patternSetupOf(manifest: Manifest | undefined, typeName: string): PatternSetup | undefined {
  const references = manifest?.types.find((entry) => entry.name === typeName)?.references ?? []
  if (!references.some((entry) => entry.name === 'declaration')) return undefined
  if (references.some((entry) => entry.name === 'input')) return { kind: 'single', targetType: '' }
  const source = references.find((entry) => entry.name === 'source')
  if (source?.accepts[0]) return { kind: 'repeating', targetType: source.accepts[0] }
  return undefined
}
